import { useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Plus, ShoppingCart, Printer, FileText, Receipt, CloudOff, AlertCircle } from "lucide-react";
import { MTopBar, ComandaBar } from "@/components/mobile/MTopBar";
import { MPage, Footer, Loading, cardCls } from "@/components/mobile/parts";
import { ReprintDialog } from "@/components/mobile/ReprintDialog";
import { MergeDialog } from "@/components/mobile/MergeDialog";
import { useComanda } from "@/hooks/useMobileData";
import { useMobile } from "@/context/MobileContext";
import { useAuth } from "@/context/AuthContext";
import { brl } from "@/lib/api";
import { activeItems, orderGroups, pad2 } from "@/lib/mobile";
import { cn } from "@/lib/utils";

function MenuCard({ icon: Icon, title, count, onClick, testId }) {
  return (
    <button onClick={onClick} data-testid={testId} className={cn(cardCls, "relative flex aspect-square flex-col items-center justify-center gap-3 p-4 text-center transition-transform active:scale-[0.97]")}>
      <span className="grid h-16 w-16 place-items-center rounded-2xl bg-[#e3f2fd] text-[#1565c0]"><Icon className="h-8 w-8" /></span>
      <span className="font-display font-semibold leading-tight text-slate-800">{title}</span>
      {count != null && <span className="absolute right-3 top-3 grid h-7 min-w-7 place-items-center rounded-full bg-[#1565c0] px-2 text-xs font-bold text-white" data-testid={`${testId}-count`}>{pad2(count)}</span>}
    </button>
  );
}

function Notices({ c, cart, pending, base }) {
  return (
    <>
      {cart > 0 && <Link to={`${base}/carrinho`} className="flex items-center gap-2 rounded-xl bg-amber-100 px-3 py-2.5 text-sm font-medium text-amber-900" data-testid="m-menu-cart-notice"><AlertCircle className="h-4 w-4" />{cart} item(ns) no carrinho ainda não enviados. Ver carrinho</Link>}
      {pending > 0 && <Link to="/m/backup" className="flex items-center gap-2 rounded-xl bg-red-100 px-3 py-2.5 text-sm font-medium text-red-800" data-testid="m-menu-offline-notice"><CloudOff className="h-4 w-4" />{pending} pedido(s) guardado(s) no celular aguardando internet</Link>}
      {c.offline && <p className="rounded-xl bg-amber-100 px-3 py-2.5 text-sm text-amber-900" data-testid="m-menu-offline-comanda">Comanda guardada no celular. Ela será criada no sistema quando a internet voltar.</p>}
    </>
  );
}

export default function MComandaMenu() {
  const { id } = useParams();
  const { c, load, error, cid } = useComanda(id);
  const { cartOf, pendingFor } = useMobile();
  const { session } = useAuth();
  const [dlg, setDlg] = useState(null);
  const nav = useNavigate();
  if (!c) return <><MTopBar title="Comanda" back="/m/pdv" />{error ? <p className="p-6 text-center text-sm text-red-600">{error}</p> : <Loading />}</>;
  const base = `/m/comanda/${id}`;
  const cart = cartOf(cid).reduce((s, i) => s + i.qty, 0);
  const pending = pendingFor(cid).length;
  const live = c.offline || c.status === "open";
  const canClose = live && !c.offline && activeItems(c).length > 0;
  return (
    <>
      <MTopBar title={`Comanda #${c.number}`} back="/m/pdv" />
      <ComandaBar c={c} />
      <MPage>
        {session.user.waiter_view && <p className="text-sm text-slate-600" data-testid="m-menu-waiter">Garçom responsável: <b className="text-[#0d47a1]">{c.opened_by || "—"}</b></p>}
        <Notices c={c} cart={cart} pending={pending} base={base} />
        {!live && <p className="rounded-xl bg-slate-200 px-3 py-2.5 text-sm" data-testid="m-menu-closed-notice">Esta comanda não está mais aberta.</p>}
        <div className="grid grid-cols-2 gap-3" data-testid="m-comanda-menu">
          <MenuCard icon={Plus} title="Adicionar Itens" count={activeItems(c).length} onClick={() => live && nav(`${base}/produtos`)} testId="m-menu-add-items" />
          <MenuCard icon={ShoppingCart} title="Ver Pedidos" count={orderGroups(c).length + pending} onClick={() => nav(`${base}/pedidos`)} testId="m-menu-orders" />
          <MenuCard icon={Printer} title="Reimprimir" onClick={() => !c.offline && setDlg("print")} testId="m-menu-reprint" />
          <MenuCard icon={FileText} title="Agrupar Comanda" onClick={() => live && !c.offline && setDlg("merge")} testId="m-menu-merge" />
        </div>
      </MPage>
      <Footer testId="m-close-footer">
        <button onClick={() => nav(`${base}/fechar`)} disabled={!canClose} className="flex w-full items-center justify-between bg-[#16a34a] px-5 py-4 text-white transition-colors hover:bg-[#15803d] disabled:opacity-60" data-testid="m-close-btn">
          <span className="flex items-center gap-2 font-display text-lg font-semibold"><Receipt className="h-5 w-5" />Realizar Fechamento</span>
          <span className="text-right"><span className="block font-mono text-xl font-bold" data-testid="m-close-total">{brl(c.paid > 0 ? c.remaining : c.total)}</span>{c.paid > 0 && <span className="text-[11px] text-white/80">restante</span>}</span>
        </button>
      </Footer>
      <ReprintDialog open={dlg === "print"} c={c} onClose={() => setDlg(null)} />
      <MergeDialog open={dlg === "merge"} c={c} onClose={() => setDlg(null)} onDone={load} />
    </>
  );
}
